import { resetWebAppState } from "@/api/privateRequests/evolutions/resetWebAppState";
import { InternalLink } from "@/components/InternalLink";
import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { canonicalUrl } from "@/config/canonical";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { Button as DsButton } from "@/design-system/Button/Button";
import {
  ExpandableSearch,
  useExpandableSearch,
} from "@/design-system/Input/ExpandableSearch";
import { useGoBack } from "@/hooks/useGoBack";
import {
  Evolution,
  EvolutionState,
  evolutionsSelector,
  fetchEvolutions,
} from "@/redux/evolutions/evolutionsSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  getUserSelector,
  userIsLoggedInSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { RecordIcon } from "@/solarIcons";
import {
  EVOLUTION_STATE_ITEMS,
  type EvolutionPickerSelection,
  type EvolutionsOverviewSort,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import type { EvolutionCategory } from "@repo/shared/types";
import { EVOLUTION_STATES_GUIDE_PATH, formatTime } from "@repo/shared/utils";
import {
  CircleCheckBig,
  CircleOff,
  Clock1,
  Info,
  LoaderCircle,
  RefreshCw,
  SquareCheck,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { EvolutionsHubCards } from "../EvolutionsHub";
import { EvolutionsMobileNav } from "../EvolutionsMobileNav";
import { ChangeStatesPopup } from "../Popups/ChangeStatesPopup";
import { ResetToWebAppStatePopup } from "../Popups/ResetToWebAppStatePopup";
import { EvolutionStateActionsBottomSheet } from "./EvolutionStateActionsBottomSheet";
import { EvolutionsOverviewContent } from "./EvolutionsOverviewContent";

// URL slug and the i18n keys of the label and the longer copy this page
// needs; the state order comes from the shared list, so the web and mobile
// pickers can't drift apart on it.
const EVO_STATE_COPY = {
  unachieved: {
    key: "unachieved",
    labelKey: "states.labels.unachieved",
    descriptionKey: "states.descriptions.unachieved",
  },
  available: {
    key: "available",
    labelKey: "states.labels.available",
    descriptionKey: "states.descriptions.available",
  },
  planned: {
    key: "planned",
    labelKey: "states.labels.planned",
    descriptionKey: "states.descriptions.planned",
  },
  inProgress: {
    key: "in-progress",
    labelKey: "states.labels.inProgress",
    descriptionKey: "states.descriptions.inProgress",
  },
  completed: {
    key: "completed",
    labelKey: "states.labels.completed",
    descriptionKey: "states.descriptions.completed",
  },
} as const;

export type EvoStateLabelKey =
  (typeof EVO_STATE_COPY)[keyof typeof EVO_STATE_COPY]["labelKey"];
export type EvoStateDescriptionKey =
  (typeof EVO_STATE_COPY)[keyof typeof EVO_STATE_COPY]["descriptionKey"];

export const evoStateItems: {
  state: EvolutionState;
  key: string;
  labelKey: EvoStateLabelKey;
  descriptionKey: EvoStateDescriptionKey;
}[] = EVOLUTION_STATE_ITEMS.map(({ state }) => ({
  state,
  ...EVO_STATE_COPY[state],
}));

const OTHER_SELECTION_LABEL_KEYS = {
  new: "picker.newEvolutions",
  expiring: "picker.expiringSoon",
  free: "common:free",
  paid: "picker.paid",
  all: "picker.all",
  expired: "expired",
} as const;

// Translated twin of the shared evolutionPickerSelectionLabel, whose English
// strings still serve mobile. Same keys the picker sidebar puts on its rows.
export const useEvolutionPickerSelectionLabel = (
  selection: EvolutionPickerSelection,
  orderedCategories: EvolutionCategory[],
): string => {
  const { t } = useTranslation(["evolutions", "common"]);
  return useMemo(() => {
    switch (selection.type) {
      case "all-included":
        return t("picker.allIncluded");
      case "state":
        return t(EVO_STATE_COPY[selection.state].labelKey);
      case "category":
        return (
          orderedCategories.find((c) => c.id === selection.categoryId)?.name ??
          t("comingSoon.evolutions")
        );
      case "other":
        return t(OTHER_SELECTION_LABEL_KEYS[selection.key]);
    }
  }, [selection, orderedCategories, t]);
};

export const getEvoStateIcon = (state: EvolutionState, size: number = 20) => {
  switch (state) {
    case "unachieved":
      return <CircleOff size={size} />;
    case "available":
      return <RecordIcon size={size} />;
    case "planned":
      return <Clock1 size={size} />;
    case "inProgress":
      return <LoaderCircle size={size} />;
    case "completed":
      return <CircleCheckBig size={size} />;
    default:
      return null;
  }
};

export const SORT_LABEL_KEYS = {
  new: "overview.sort.new",
  expiry: "overview.sort.expiry",
  price: "overview.sort.price",
  "matches-asc": "overview.sort.matches",
  "biggest-upgrade": "overview.sort.biggestUpgrade",
} as const satisfies Record<EvolutionsOverviewSort, string>;

export const EvolutionsOverviewPage = () => {
  const { t } = useTranslation(["evolutions", "common"]);
  const overviewSeo = useSeoCopy("evolutionsOverview");
  // Indexed landing page — a visitor from search has no history to go back to.
  const goBack = useGoBack();
  const { evolutions, status } = useSelector(evolutionsSelector);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedEvolutions, setSelectedEvolutions] = useState<Evolution[]>([]);
  const [showChangeStatesModal, setShowChangeStatesModal] = useState(false);
  const [showResetToWebAppStateModal, setShowResetToWebAppStateModal] =
    useState(false);
  const [showStateActionsSheet, setShowStateActionsSheet] = useState(false);
  const canUseClubImport = useSelector(canUseClubImportSelector);
  const { user } = useSelector(getUserSelector);
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const [searchTerm, setSearchTerm] = useState("");
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  // On a phone the search is a button until tapped, then takes the band over.
  const search = useExpandableSearch(searchTerm, isMobile);
  const searchTakesOver = search.takesOver;

  const dispatch = useDispatch<AppDispatch>();

  const handleChangeStatusClick = () => {
    if (isLoggedIn) {
      setShowChangeStatesModal(true);
    } else {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "change-evo-state",
        }),
      );
    }
  };
  const handleCompleteChangeStates = () => {
    setShowChangeStatesModal(false);
    setSelectedEvolutions([]);
    setSelectMode(false);
  };

  const handleSelectEvo = (evo: Evolution) => {
    // check if evo is already selected
    if (selectedEvolutions.includes(evo)) {
      setSelectedEvolutions(selectedEvolutions.filter((e) => e.id !== evo.id));
    } else {
      setSelectedEvolutions([...selectedEvolutions, evo]);
    }
  };

  const handleSelectMode = () => {
    if (selectMode) {
      setSelectedEvolutions([]);
    }
    setSelectMode(!selectMode);
  };

  // The sheet always enters select mode — unlike the toggle above, it can be
  // opened while select mode is already on, and must not turn it back off.
  const handleEnterSelectMode = () => {
    setSelectedEvolutions([]);
    setSelectMode(true);
  };

  const handleShowResetToWebAppStateModal = () => {
    if (isLoggedIn) {
      setShowResetToWebAppStateModal(true);
    } else {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "reset-web-app-state",
        }),
      );
    }
  };

  const handleResetToWebAppState = async () => {
    try {
      await resetWebAppState({
        overridePlanned: true,
      });
      dispatch(
        addAlert({
          type: "success",
          description: t("overview.alerts.resetSuccess"),
        }),
      );
      setShowResetToWebAppStateModal(false);
      dispatch(fetchEvolutions());
    } catch (error) {
      dispatch(
        addAlert({
          type: "error",
          description: t("overview.alerts.resetFailed"),
        }),
      );
    }
  };

  return (
    <main className="mx-auto flex min-h-screen flex-col bg-background">
      <HeroSection
        helmetTitle={overviewSeo.title}
        helmetDescription={overviewSeo.description}
        subtitle={overviewSeo.subtitle}
        helmetCanonicalLink={canonicalUrl("/evolutions")}
        back={goBack}
        shouldBeInTheDocumentFlow
      >
        <div className="my-8 hidden mobile:block">
          <EvolutionsHubCards activeEvoCard="overview" />
        </div>
      </HeroSection>
      <EvolutionsMobileNav active="overview" />
      {/* The 2.0 band, as on /players: search left, the state actions right. */}
      <div className="flex h-15 w-full justify-center bg-surface py-2 touch-device:py-4">
        <div className="mx-auto flex w-full max-w-content items-center gap-x-2 px-4 large:px-16 touch-device:px-4">
          <div className="flex flex-1 items-center gap-x-2">
            <ExpandableSearch
              search={search}
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t("overview.searchPlaceholder")}
            />
            {isLoggedIn && !searchTakesOver && (
              <>
                <DsButton
                  variant="secondary"
                  size="small"
                  icon={<RefreshCw className="size-4" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowResetToWebAppStateModal();
                  }}
                >
                  {canUseClubImport
                    ? t("overview.resetToWebAppState")
                    : t("overview.resetStates")}
                </DsButton>
                <Tooltip
                  content={
                    <div className="flex w-64 flex-col gap-y-2 text-xs">
                      <p className="text-foreground-body">
                        {t("overview.statesTroubleHint")}
                      </p>
                      {user?.lastImportedAt && (
                        <p className="text-foreground-muted">
                          {t("overview.lastImportDetected", {
                            time: formatTime(Date.now() - user.lastImportedAt),
                          })}
                        </p>
                      )}
                    </div>
                  }
                  side="bottom"
                  delayDuration={100}
                >
                  <Info className="size-4 shrink-0 text-foreground-muted hover:text-foreground" />
                </Tooltip>
              </>
            )}
            {/*
              Changing states by hand is retired for now; kept to bring back
              if users ask for it. Desktop:

              {!isMobile &&
                (selectMode ? (
                  <>
                    <DsButton variant="secondary" size="small" onClick={handleSelectMode}>
                      {t("common:cancel")}
                    </DsButton>
                    <DsButton
                      variant="primary"
                      size="small"
                      disabled={selectedEvolutions.length === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChangeStatusClick();
                      }}
                    >
                      {t("overview.changeStatesCount", { count: selectedEvolutions.length })}
                    </DsButton>
                  </>
                ) : (
                  <DsButton
                    variant="secondary"
                    size="small"
                    icon={<SquareCheck className="size-4" />}
                    onClick={handleSelectMode}
                  >
                    {t("overview.changeStates")}
                  </DsButton>
                ))}

              Mobile, on the right of the band:

              {isMobile && !searchTakesOver && (
                <DsButton
                  variant="secondary"
                  size="small"
                  icon={<SquareCheck className="size-4" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStateActionsSheet(true);
                  }}
                >
                  {t("overview.manageStates")}
                </DsButton>
              )}
            */}
          </div>
          {/* Desktop only; phones carry it on the sort row. */}
          <span className="flex min-w-0 text-sm max-mobile:hidden">
            <InternalLink to={EVOLUTION_STATES_GUIDE_PATH} truncate>
              {t("overview.statesHelp")}
            </InternalLink>
          </span>
        </div>
      </div>
      <EvolutionsOverviewContent
        evolutions={evolutions}
        isLoading={status === "pending" || status === "idle"}
        selectMode={selectMode}
        selectedEvolutions={selectedEvolutions}
        onSelectEvo={handleSelectEvo}
        searchTerm={searchTerm}
        handleSelectMode={handleSelectMode}
        handleChangeStatusClick={handleChangeStatusClick}
      />
      {/* Retired with Change States, see the band above.
      {isMobile && (
        <EvolutionStateActionsBottomSheet
          isOpen={showStateActionsSheet}
          onClose={() => setShowStateActionsSheet(false)}
          onChangeStates={handleEnterSelectMode}
          onResetStates={handleShowResetToWebAppStateModal}
          canUseClubImport={canUseClubImport}
          lastImportedAt={user?.lastImportedAt}
        />
      )}
      */}
      {showChangeStatesModal && (
        <ChangeStatesPopup
          onComplete={handleCompleteChangeStates}
          onClose={() => setShowChangeStatesModal(false)}
          selectedEvolutions={selectedEvolutions}
        />
      )}
      {showResetToWebAppStateModal && (
        <ResetToWebAppStatePopup
          onComplete={handleResetToWebAppState}
          onClose={() => {
            setShowResetToWebAppStateModal(false);
          }}
        />
      )}
    </main>
  );
};
